import { Role } from '@customTypes/types';
import { ClubPhotoAccordion } from '@features/work/components/slider/ClubPhotoAccordion.tsx';
import { ClubVideoAccordion } from '@features/work/components/slider/ClubVideoAccordion.tsx';
import { ExperienceSlider } from '@features/work/components/slider/ExperienceSlider.tsx';
import {
  ClockIcon,
  MapPinIcon,
  PhotoIcon
} from '@heroicons/react/24/outline';
import {
  Accordion, AccordionControl,
  Avatar,
  Flex, Group,
  Space,
  Stack,
  Text,
  Title
} from '@mantine/core';
import { useProfileStore } from '@shared/store/profileStore.ts';
import dayjs from 'dayjs';
import { FormattedMessage, useIntl } from 'react-intl';

import { useArtistConditionView } from '@/features/work/view/artist-full-profile/hooks/useArtistConditionView';
import { PageLoading } from '@/pages/PageLoading';
import { resolveImagePath } from '@/shared/helper/resolveImagePath';
import { translate } from '@/shared/i18n/translate';
import {
  LBAccordionContainer,
  LBBadge,
  LBListContainer,
  LBListItem,
} from '@/shared/ui-kit/components';

export const ArtistFullProfile = ({
  artistProfileId
}: {
  artistProfileId: string;
}) => {
  const { formatMessage } = useIntl();
  const {
    state: { isLoading, fullProfile, profilePhoto }
  } = useArtistConditionView({ profileId: artistProfileId });

  const profile = useProfileStore((state) => state.profile);
  const isAdmin = profile?.role === Role.admin;

  if (isLoading) return <PageLoading />;

  return (
    <div>
      <Flex align='center' gap={16}>
        <Avatar
          size={120}
          src={
            profilePhoto.data?.data[0]?.filePath &&
            resolveImagePath(profilePhoto.data.data[0]?.filePath)
          }
        />
        <Flex direction='column' gap={8}>
          <Flex direction='column'>
            <Title order={3}>
              {fullProfile.data?.data.info?.firstName}
              {isAdmin ? `(${artistProfileId})` : null}
            </Title>
            <Text fz={14}>
              {dayjs().diff(fullProfile.data?.data.info?.birthDate, 'years')}{' '}
              {translate('commons.years')}
            </Text>
          </Flex>
          <Flex>
            <MapPinIcon width={18} />
            <Text fz={14}>
              {fullProfile.data?.data.info?.countryOfResidence}
            </Text>
          </Flex>
          <LBBadge>
            {fullProfile.data?.data.status && (
              <FormattedMessage
                id={`commons.artistStatuses.${fullProfile.data?.data.status}`}
              />
            )}
          </LBBadge>
        </Flex>
      </Flex>
      <Space h={16} />
      <Stack gap={16}>
        <Stack gap={4}>

          <LBAccordionContainer>
          <Accordion.Item value='photos_artist'>
            <AccordionControl>
              <Group align='center' gap='xs' p={2}>
                <PhotoIcon width={17} />
                <Text c='#4f5053' fw={300} fz={14}>
                  {translate('commons.photo')}
                </Text>
              </Group>
            </AccordionControl>
            <ClubPhotoAccordion
              photos={ fullProfile.data?.data.photos.map((p) => p.filePath) ?? []}
            />
          </Accordion.Item>


            <Accordion.Item value='videos_artist'>
              <AccordionControl>
                <Group align='center' gap='xs' p={2}>
                  <PhotoIcon width={17} />
                  <Text c='#4f5053' fw={300} fz={14}>
                    {translate('commons.video')}
                  </Text>
                </Group>
              </AccordionControl>
              <ClubVideoAccordion
                videos={fullProfile?.data?.data.videos ?? []} />
            </Accordion.Item>
            <Accordion.Item value='experience-artist'>
              <AccordionControl>
                <Group align='center' gap='xs' p={2}>
                  <ClockIcon width={17} />
                  <Text c='#4f5053' fw={300} fz={14}>
                    {translate('commons.experience')}
                  </Text>
                </Group>
              </AccordionControl>
              <ExperienceSlider
                data={fullProfile.data?.data.workExperience.workHistory ?? []}
              />
            </Accordion.Item>
          </LBAccordionContainer>

        </Stack>
        <Stack gap={4}>
          <LBListContainer
            label={formatMessage({ id: 'commons.wantedVacancy' })}
          >
            {fullProfile.data?.data.info?.isLookingForStripVacancy && (
              <LBListItem>
                <Text fz={14}>
                  {translate('commons.artistJobs.Strip')}{' '}
                  {translate('commons.dancer')}
                </Text>
              </LBListItem>
            )}
            {fullProfile.data?.data.info?.isLookingForGoGoVacancy && (
              <LBListItem>
                <Text fz={14}>
                  {translate('commons.artistJobs.GoGo')}{' '}
                  {translate('commons.dancer')}
                </Text>
              </LBListItem>
            )}
            {fullProfile.data?.data.info?.isLookingForHostessVacancy && (
              <LBListItem>
                <Text fz={14}>{translate('commons.artistJobs.Hostess')}</Text>
              </LBListItem>
            )}
          </LBListContainer>
          <LBListContainer label={formatMessage({ id: 'commons.citizenship' })}>
            {fullProfile.data?.data.info?.citizenships?.map((item) => (
              <LBListItem key={item}>
                <Text fz={14}>{item}</Text>
              </LBListItem>
            ))}
          </LBListContainer>
          <LBListContainer label={formatMessage({ id: 'commons.language' })}>
            {fullProfile.data?.data.languages?.map((item) => (
              <LBListItem key={item.name}>
                <Text fz={14}>{item.name}</Text>
                <Text fz={14}>
                  {translate(`commons.languages.${item.level}`)}
                </Text>
              </LBListItem>
            ))}
          </LBListContainer>
          <LBListContainer label={formatMessage({ id: 'commons.parametres' })}>
            <LBListItem>
              <Text fz={14}>
                {translate('commons.artistParametres.height')}
              </Text>
              <Text fz={14}>
                {fullProfile.data?.data.parameters?.height} см
              </Text>
            </LBListItem>
            <LBListItem>
              <Text fz={14}>
                {translate('commons.artistParametres.weight')}
              </Text>
              <Text fz={14}>
                {fullProfile.data?.data.parameters?.weight} кг
              </Text>
            </LBListItem>
            <LBListItem>
              <Text fz={14}>{translate('commons.artistParametres.body')}</Text>
              <Text fz={14}>
                {fullProfile.data?.data.parameters?.chest} /
                {fullProfile.data?.data.parameters?.waist} /
                {fullProfile.data?.data.parameters?.hips} см
              </Text>
            </LBListItem>
          </LBListContainer>
          <LBListContainer label={formatMessage({ id: 'commons.about' })}>
            <Text fz={14} style={{ whiteSpace: 'pre-wrap' }}>
              {fullProfile.data?.data?.about}
            </Text>
          </LBListContainer>
        </Stack>
      </Stack>
    </div>
  );
};
